// TODO : display errors on this page

import { CronExpressionParser } from "cron-parser";
import { limit, orderBy, where } from "firebase/firestore";
import moment from "moment-timezone";
import React from "react";
import {
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    Col,
    Dropdown,
    DropdownButton,
    ListGroup,
    Modal,
    Row,
} from "react-bootstrap";
import {
    FaChevronRight,
    FaClock,
    FaCode,
    FaCog,
    FaExclamationTriangle,
    FaPlay,
    FaTrash,
    FaTrashRestore,
} from "react-icons/fa";
import { MdBuild } from "react-icons/md";
import { Link, useNavigate, useParams } from "react-router";

import * as api from "../../api/api";
import {
    LocationDocument,
    useLocationDocument,
    useResultCollectionGroup,
    useRoutineCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CodeBlock from "../../components/CodeBlock";
import DevMenuButton from "../../components/DevMenuButton";
import DroneCardList from "../../components/DroneCardList/index.jsx";
import FirestoreDevMenuItem from "../../components/FirestoreDevMenuItem";
import Layout, { Title } from "../../components/Layout/Layout";
import LogDevMenuItem from "../../components/LogDevMenuItem";
import MapButton from "../../components/MapButton";
import MissionList from "../../components/MissionList";
import ResultCardList from "../../components/ResultCardList/index.jsx";
import {
    hasPublishedFiles,
    hasUnpublishedFiles,
} from "../../components/ResultsList/index.jsx";
import StorageDevMenuItem from "../../components/StorageDevMenuItem/index.jsx";
import TimeDisplay from "../../components/TimeDisplay/index.jsx";
import { useUser } from "../../contexts/user_provider";
import { SPECIAL_UI_ACCESS_ORGANIZATION_ID } from "../../organizations.js";
import urls from "../../urls.jsx";

function LocationMissionList(props) {
    const { organizationId, locationId } = useParams();
    const [location] = useLocationDocument(organizationId, locationId);
    const timezone = location?.timezone || "UTC";

    return (
        <MissionList
            title="Missions"
            className={props.className}
            routines={props.routines}
            organizationId={organizationId}
            locationId={locationId}
            missions={props.missions}
        >
            {props.routines
                .filter((routine) => routine.enabled)
                .sort((a, b) => {
                    const aNext = CronExpressionParser.parse(a.interval, {
                        tz: timezone,
                    })
                        .next()
                        .toDate();
                    const bNext = CronExpressionParser.parse(b.interval, {
                        tz: timezone,
                    })
                        .next()
                        .toDate();
                    return aNext - bNext;
                })
                .map((routine) => {
                    let timeDisplayContent;
                    try {
                        const nextOccurrence = CronExpressionParser.parse(
                            routine.interval,
                            { tz: timezone },
                        )
                            .next()
                            .toDate();
                        const timestamp = moment(nextOccurrence).unix();

                        timeDisplayContent = (
                            <TimeDisplay
                                timestamp={timestamp}
                                format="ddd HH:mm"
                                organizationId={organizationId}
                                locationId={locationId}
                            />
                        );
                    } catch (error) {
                        console.error(
                            `Error parsing interval for routine ${routine.id}:`,
                            error,
                        );
                        timeDisplayContent = "Invalid"; // Display 'Invalid' if parsing fails
                    }

                    return (
                        <ListGroup.Item
                            className="px-4 py-2 d-flex justify-content-between align-items-start"
                            key={`location-routine-${routine.id}`}
                            as={Link}
                            to={urls.routine(
                                organizationId,
                                locationId,
                                routine.id,
                            )}
                            action
                        >
                            <div>
                                <FaClock
                                    className="me-4 text-primary"
                                    style={{ marginTop: "-2px" }}
                                />
                                {routine.name} by {routine.droneId}
                            </div>
                            <div>
                                <Badge
                                    pill
                                    bg="primary"
                                    className="me-1"
                                    style={{ marginTop: "-2px" }}
                                >
                                    <FaClock
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    {timeDisplayContent}
                                </Badge>
                                <FaChevronRight style={{ color: "#b9b9b9" }} />
                            </div>
                        </ListGroup.Item>
                    );
                })}
        </MissionList>
    );
}

function AdminLocationButton(props) {
    const { organizationId, locationId } = useParams();
    const { getUserIsAdmin } = useUser();
    const userIsAdmin = getUserIsAdmin();
    const canAccessLocationSettings =
        userIsAdmin || organizationId === SPECIAL_UI_ACCESS_ORGANIZATION_ID;

    return (
        <ButtonGroup className={props.className}>
            <Button
                variant="primary"
                as={Link}
                to={urls.locationStart(organizationId, locationId)}
            >
                <FaPlay
                    className="me-2"
                    style={{
                        marginTop: "-2px",
                    }}
                />
                Start
            </Button>
            {canAccessLocationSettings && (
                <DropdownButton
                    variant="primary"
                    align="end"
                    as={ButtonGroup}
                    title=""
                >
                    <Dropdown.Item
                        as={Link}
                        to={urls.locationSettings(organizationId, locationId)}
                    >
                        <FaCog
                            className="me-2"
                            style={{
                                marginTop: "-2px",
                            }}
                        />
                        Settings
                    </Dropdown.Item>
                    {userIsAdmin && (
                        <Dropdown.Item
                            onClick={props.showDeleteModal}
                            className="text-danger"
                        >
                            <FaTrash
                                className="me-2"
                                style={{
                                    marginTop: "-2px",
                                }}
                            />
                            Delete
                        </Dropdown.Item>
                    )}
                </DropdownButton>
            )}
        </ButtonGroup>
    );
}

export default function LocationPage() {
    const { organizationId, locationId } = useParams();
    const [location] = useLocationDocument(organizationId, locationId);
    const [droneIds] = api.useDroneIdsFromLocation(organizationId, locationId);
    const [routines] = useRoutineCollection(organizationId, locationId);
    const [missions] = api.useMissionsFromLocation(
        organizationId,
        locationId,
        10,
    );
    const [view, setView] = React.useState("default");
    const { getUserIsAdmin } = useUser();
    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const navigate = useNavigate();

    const [results] = useResultCollectionGroup([
        where("organizationId", "==", organizationId),
        where("locationId", "==", locationId),
        where("hasFiles", "==", true),
        orderBy("timestamp", "desc"),
        limit(50),
    ]);

    const { publishedResults, unpublishedResults } = React.useMemo(() => {
        if (!results) {
            return { publishedResults: [], unpublishedResults: [] };
        }

        const seenHashes = new Set();
        const seenPublishedApps = new Set();
        const seenUnpublishedApps = new Set();

        const publishedResults = [];
        const unpublishedResults = [];

        for (const result of results) {
            const appId = result.app?.id;
            if (!appId) continue;

            const originalFiles = result.files ?? [];

            // Deduplicate by hash (latest wins)
            const files = originalFiles.filter((file) => {
                if (!file.hash) return false;

                if (seenHashes.has(file.hash)) {
                    return false;
                }

                seenHashes.add(file.hash);
                return true;
            });

            if (files.length === 0) continue;

            const hasPublished = hasPublishedFiles(files, false);
            const hasUnpublished = hasUnpublishedFiles(files);

            if (hasPublished) {
                if (seenPublishedApps.has(appId)) continue;
                seenPublishedApps.add(appId);
                publishedResults.push({
                    ...result,
                    files,
                });
            }

            if (hasUnpublished) {
                if (seenUnpublishedApps.has(appId)) continue;
                seenUnpublishedApps.add(appId);
                unpublishedResults.push({
                    ...result,
                    files,
                });
            }
        }

        return {
            publishedResults,
            unpublishedResults,
        };
    }, [results]);

    function hideDeleteModal() {
        setDeleteModalVisible(false);
    }

    function showDeleteModal() {
        setDeleteModalVisible(true);
    }

    function getEnabledRoutineIndicatorVariant() {
        if (routines.filter((routine) => routine.enabled).length === 0)
            return "outline-danger";
        return "outline-success";
    }

    function getRoutineIndicatorVariant() {
        if (routines.length === 0) return "outline-danger";
        return "outline-success";
    }

    return (
        <Layout>
            <title>{`${location?.name} | MyCorvus`}</title>
            <Breadcrumbs path={breadcrumbPaths.location} location={location} />
            <Modal show={deleteModalVisible} onHide={hideDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Location</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Delete location <b>{location?.name}</b>?
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={hideDeleteModal}
                    >
                        Cancel
                    </Button>
                    <Dropdown as={ButtonGroup} align={{ lg: "start" }}>
                        <Button
                            variant="danger"
                            onClick={() => {
                                hideDeleteModal();
                                api.deleteLocation(organizationId, locationId);
                            }}
                        >
                            <FaTrash
                                className="me-2"
                                style={{ marginTop: "-2px" }}
                            />
                            Delete
                        </Button>
                    </Dropdown>
                </Modal.Footer>
            </Modal>
            <Card>
                <Title
                    title={location ? location.name : "Location"}
                    backButtonUrl={urls.organization(organizationId)}
                >
                    {getUserIsAdmin() && (
                        <DevMenuButton className="ms-2 mb-1">
                            <Dropdown.Item
                                onClick={() =>
                                    setView(
                                        view === "default" ? "data" : "default",
                                    )
                                }
                            >
                                <FaCode
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                {view === "default"
                                    ? "View Data"
                                    : "View Default"}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() =>
                                    navigate(
                                        urls.areaBuilder(
                                            organizationId,
                                            locationId,
                                        ),
                                    )
                                }
                            >
                                <MdBuild
                                    className="me-2"
                                    style={{
                                        marginTop: "-2px",
                                    }}
                                />
                                Area builder
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <FirestoreDevMenuItem
                                as={Dropdown.Item}
                                path={
                                    LocationDocument(organizationId, locationId)
                                        .path
                                }
                            />
                            <StorageDevMenuItem
                                as={Dropdown.Item}
                                path={
                                    LocationDocument(organizationId, locationId)
                                        .path
                                }
                            />
                            <LogDevMenuItem
                                as={Dropdown.Item}
                                name="location"
                                value={locationId}
                            />
                        </DevMenuButton>
                    )}
                    <MapButton
                        url={`${urls.locationMap(organizationId, locationId)}${getUserIsAdmin() ? "?inspect" : ""}`}
                        className="ms-2 mb-1"
                    />
                    <AdminLocationButton
                        view={view}
                        setView={setView}
                        showDeleteModal={showDeleteModal}
                        className="ms-2 mb-1"
                    />
                </Title>
                {getUserIsAdmin() && location?.deleted && (
                    <Card.Body className="pb-4 pt-0 px-4">
                        <Alert variant="danger" className="m-0 py-2">
                            <div className="d-flex justify-content-between align-items-center">
                                <span>
                                    <FaExclamationTriangle
                                        className="me-2"
                                        style={{ marginTop: "-2px" }}
                                    />
                                    This location is deleted!
                                </span>

                                <Button
                                    variant="primary"
                                    onClick={() =>
                                        api.restoreLocation(
                                            organizationId,
                                            locationId,
                                        )
                                    }
                                >
                                    <FaTrashRestore className="me-2" />
                                    Restore
                                </Button>
                            </div>
                        </Alert>
                    </Card.Body>
                )}
                {getUserIsAdmin() && (
                    <ListGroup variant="flush">
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Assistent</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    <h5 className="d-inline">
                                        <Button
                                            variant={
                                                location?.assistant?.enabled
                                                    ? "success"
                                                    : "danger"
                                            }
                                            size="sm"
                                            className="me-2"
                                        >
                                            {location?.assistant?.enabled
                                                ? "Enabled"
                                                : "Disabled"}
                                        </Button>
                                        <Button
                                            variant={getRoutineIndicatorVariant()}
                                            size="sm"
                                            className="me-2"
                                        >
                                            {routines.length || 0} routines
                                        </Button>
                                        <Button
                                            variant={getEnabledRoutineIndicatorVariant()}
                                            size="sm"
                                            className="me-2"
                                        >
                                            {routines.filter(
                                                (routine) => routine.enabled,
                                            ).length || 0}{" "}
                                            enabled
                                        </Button>
                                    </h5>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                        <ListGroup.Item className="px-4">
                            <Row>
                                <Col xs={12} md={3}>
                                    <b>Timezone</b>
                                </Col>
                                <Col xs={12} md={9}>
                                    <h5 className="d-inline">
                                        <Button
                                            variant="outline-secondary"
                                            as={Link}
                                            to={urls.locationSettings(
                                                organizationId,
                                                locationId,
                                            )}
                                            size="sm"
                                            className="me-2"
                                        >
                                            {location?.timezone || "UTC"}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="me-2"
                                        >
                                            {/* Display hour:minute only */}
                                            {moment()
                                                .tz(location?.timezone || "UTC")
                                                .format("HH:mm")}
                                        </Button>
                                    </h5>
                                </Col>
                            </Row>
                        </ListGroup.Item>
                    </ListGroup>
                )}
                {view === "data" && (
                    <Card.Body className="p-4 pt-0">
                        <CodeBlock name="Location" code={location} />
                    </Card.Body>
                )}
            </Card>
            <Row className="mt-4">
                <Col xs={12} md={8}>
                    <LocationMissionList
                        organizationId={organizationId}
                        locationId={locationId}
                        limit={10}
                        className="mb-4"
                        missions={missions}
                        routines={routines}
                    />
                </Col>
                <Col xs={12} md={4}>
                    <ResultCardList
                        results={publishedResults}
                        className="mb-3 shadow-sm"
                        title="Latest results"
                        showTimestamp={true}
                        url={urls.locationResults(organizationId, locationId)}
                    />
                    {getUserIsAdmin() && unpublishedResults.length > 0 && (
                        <ResultCardList
                            results={unpublishedResults}
                            className="mb-3 shadow-sm"
                            title="Latest unpublished results"
                            showTimestamp={true}
                        />
                    )}
                    <DroneCardList
                        className="mt-4"
                        droneIds={droneIds}
                        organizationId={organizationId}
                        locationId={locationId}
                    />
                </Col>
            </Row>
        </Layout>
    );
}
